import { WifiHigh, Clock, MapPin, NavigationArrow, Sparkle, ListChecks, Storefront, Phone, Star } from "@phosphor-icons/react";
import { CopyButton } from "@/components/common";

const Card = ({ icon: Icon, title, children, testId }) => (
  <section className="rise rounded-3xl border p-5" style={{ background: "var(--t-card)", borderColor: "var(--t-border)" }} data-testid={testId}>
    <div className="mb-3 flex items-center gap-2">
      <span className="grid h-9 w-9 place-items-center rounded-full" style={{ background: "var(--t-soft)", color: "var(--t-primary)" }}><Icon size={18} weight="bold" /></span>
      <h4 className="text-lg font-medium" style={{ color: "var(--t-text)" }}>{title}</h4>
    </div>
    {children}
  </section>
);

const muted = { color: "var(--t-muted)" };
const text = { color: "var(--t-text)" };

export function InfoBlocks({ info }) {
  const mapsHref = info.map_url || (info.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(info.address)}` : "");
  return (
    <div className="space-y-4" data-testid="guest-info-unlocked">
      {(info.wifi_name || info.wifi_password) && (
        <Card icon={WifiHigh} title="Wi-Fi" testId="guest-wifi-card">
          <div className="grid gap-2">
            <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: "var(--t-bg)" }}>
              <div><p className="text-xs" style={muted}>Network</p><p className="font-semibold" style={text} data-testid="guest-wifi-name">{info.wifi_name}</p></div>
              <CopyButton value={info.wifi_name} testId="copy-wifi-name" className="text-sm font-semibold text-[var(--t-primary)]" />
            </div>
            <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: "var(--t-bg)" }}>
              <div><p className="text-xs" style={muted}>Password</p><p className="font-mono font-semibold" style={text} data-testid="guest-wifi-password">{info.wifi_password}</p></div>
              <CopyButton value={info.wifi_password} testId="copy-wifi-password" done="Password copied" className="text-sm font-semibold text-[var(--t-primary)]" />
            </div>
          </div>
        </Card>
      )}
      <Card icon={Clock} title="Check-in & check-out" testId="guest-times-card">
        <div className="grid grid-cols-2 gap-2">
          {[["Check-in from", info.checkin_time], ["Check-out by", info.checkout_time]].map(([l, v]) => (
            <div key={l} className="rounded-2xl px-4 py-3" style={{ background: "var(--t-bg)" }}><p className="text-xs" style={muted}>{l}</p><p className="font-display text-2xl" style={text}>{v || "—"}</p></div>
          ))}
        </div>
      </Card>
      {(info.address || info.directions) && (
        <Card icon={MapPin} title="Getting here" testId="guest-directions-card">
          {info.address && <p className="font-semibold" style={text}>{info.address}</p>}
          {info.directions && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed" style={muted}>{info.directions}</p>}
          {mapsHref && (
            <a href={mapsHref} target="_blank" rel="noreferrer" data-testid="open-in-maps-button" className="mt-4 inline-flex h-12 items-center gap-2 rounded-full px-5 font-semibold" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>
              <NavigationArrow size={18} weight="fill" /> Open in Maps
            </a>
          )}
        </Card>
      )}
      {info.amenities?.length > 0 && (
        <Card icon={Sparkle} title="What's here" testId="guest-amenities-card">
          <div className="flex flex-wrap gap-2">{info.amenities.map((a) => <span key={a} className="rounded-full border px-3 py-1.5 text-sm" style={{ borderColor: "var(--t-border)", ...text }}>{a}</span>)}</div>
        </Card>
      )}
      {info.rules?.length > 0 && (
        <Card icon={ListChecks} title="House rules" testId="guest-rules-card">
          <ul className="space-y-2">{info.rules.map((r, i) => <li key={i} className="flex gap-3 text-sm" style={text}><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--t-primary)" }} />{r}</li>)}</ul>
        </Card>
      )}
      {info.nearby?.some((n) => !n.category) && (
        <Card icon={Storefront} title="Nearby" testId="guest-nearby-card">
          <div className="divide-y" style={{ borderColor: "var(--t-border)" }}>{info.nearby.filter((n) => !n.category).map((n, i) => <div key={i} className="py-2.5"><p className="font-medium" style={text}>{n.name}</p>{n.note && <p className="text-sm" style={muted}>{n.note}</p>}</div>)}</div>
        </Card>
      )}
      {info.emergency?.length > 0 && (
        <Card icon={Phone} title="Emergency contacts" testId="guest-emergency-card">
          <div className="space-y-2">{info.emergency.map((c, i) => (
            <a key={i} href={`tel:${c.phone}`} className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: "var(--t-bg)" }}>
              <span className="font-medium" style={text}>{c.label}</span><span className="text-sm font-semibold" style={{ color: "var(--t-primary)" }}>{c.phone}</span>
            </a>))}
          </div>
        </Card>
      )}
      {info.review_url && (
        <a href={info.review_url} target="_blank" rel="noreferrer" data-testid="google-review-button" className="flex h-14 items-center justify-center gap-2 rounded-full border-2 font-semibold" style={{ borderColor: "var(--t-primary)", color: "var(--t-primary)" }}>
          <Star size={18} weight="fill" /> Leave us a Google review
        </a>
      )}
    </div>
  );
}
